import { rootApi } from '../rootApi';

/**
 * Garanti Denetim Süreci API'si.
 * Base path: v1/WarrantyAudit
 * Tüm yanıtlar ResponseInfo<T> zarfı ile döner; transformResponse ile `data` çıkarılır.
 */

const unwrap = (response, fallback = null) => {
    if (response && typeof response === 'object' && 'data' in response) {
        return response.data ?? fallback;
    }
    return response ?? fallback;
};

const unwrapArray = (response) => {
    const data = unwrap(response, []);
    return Array.isArray(data) ? data : [];
};

export const warrantyAuditApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        // ---- A. Tanım Yönetimi (Kriter Ağacı) ----
        getWarrantyAuditCriteria: builder.query({
            query: ({ languageId, includeInactive = true, includeTranslations = false } = {}) => ({
                url: 'v1/WarrantyAudit/criteria',
                method: 'GET',
                params: {
                    ...(languageId ? { languageId } : {}),
                    includeInactive,
                    includeTranslations // true ise her düğüme 6 dilin translations listesi eklenir
                }
            }),
            transformResponse: (response) => unwrapArray(response),
            providesTags: ['WarrantyAudit']
        }),

        createWarrantyAuditCriteria: builder.mutation({
            query: (body) => ({
                url: 'v1/WarrantyAudit/criteria',
                method: 'POST',
                body
            }),
            invalidatesTags: ['WarrantyAudit']
        }),
        updateWarrantyAuditCriteria: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `v1/WarrantyAudit/criteria/${id}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['WarrantyAudit']
        }),
        deleteWarrantyAuditCriteria: builder.mutation({
            query: ({ id, languageId } = {}) => ({
                url: `v1/WarrantyAudit/criteria/${id}`,
                method: 'DELETE',
                params: { ...(languageId ? { languageId } : {}) }
            }),
            invalidatesTags: ['WarrantyAudit']
        }),

        createWarrantyAuditSubCriteria: builder.mutation({
            query: (body) => ({
                url: 'v1/WarrantyAudit/sub-criteria',
                method: 'POST',
                body
            }),
            invalidatesTags: ['WarrantyAudit']
        }),
        updateWarrantyAuditSubCriteria: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `v1/WarrantyAudit/sub-criteria/${id}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['WarrantyAudit']
        }),
        deleteWarrantyAuditSubCriteria: builder.mutation({
            query: ({ id, languageId } = {}) => ({
                url: `v1/WarrantyAudit/sub-criteria/${id}`,
                method: 'DELETE',
                params: { ...(languageId ? { languageId } : {}) }
            }),
            invalidatesTags: ['WarrantyAudit']
        }),

        createWarrantyAuditEvaluation: builder.mutation({
            query: (body) => ({
                url: 'v1/WarrantyAudit/evaluation',
                method: 'POST',
                body
            }),
            invalidatesTags: ['WarrantyAudit']
        }),
        updateWarrantyAuditEvaluation: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `v1/WarrantyAudit/evaluation/${id}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['WarrantyAudit']
        }),
        deleteWarrantyAuditEvaluation: builder.mutation({
            query: ({ id, languageId } = {}) => ({
                url: `v1/WarrantyAudit/evaluation/${id}`,
                method: 'DELETE',
                params: { ...(languageId ? { languageId } : {}) }
            }),
            invalidatesTags: ['WarrantyAudit']
        }),

        // ---- B. Denetim Formu / Sonuç / Geçmiş ----
        getWarrantyAuditTemplate: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/WarrantyAudit/template',
                method: 'GET',
                params: { ...(languageId ? { languageId } : {}) }
            }),
            transformResponse: (response) => unwrapArray(response),
            providesTags: ['WarrantyAudit']
        }),

        getWarrantyAuditScoreBands: builder.query({
            query: ({ languageId } = {}) => ({
                url: 'v1/WarrantyAudit/score-bands',
                method: 'GET',
                params: { ...(languageId ? { languageId } : {}) }
            }),
            transformResponse: (response) => unwrapArray(response),
            providesTags: ['WarrantyAudit']
        }),

        createWarrantyAuditResult: builder.mutation({
            query: (body) => ({
                url: 'v1/WarrantyAudit/results',
                method: 'POST',
                body
            }),
            transformResponse: (response) => unwrap(response),
            invalidatesTags: ['WarrantyAudit', 'WarrantyAuditResult']
        }),

        uploadWarrantyAuditDocument: builder.mutation({
            query: ({ warrantyAuditResultId, file, description }) => {
                const formData = new FormData();
                formData.append('WarrantyAuditResultId', warrantyAuditResultId);
                formData.append('File', file);
                formData.append('Description', description || file?.name || 'Garanti Denetim Dosyası');
                return {
                    url: 'v1/WarrantyAudit/UploadWarrantyAuditDocument',
                    method: 'POST',
                    body: formData
                };
            },
            invalidatesTags: (result, error, { warrantyAuditResultId }) => [{ type: 'WarrantyAuditResult', id: `documents-${warrantyAuditResultId}` }]
        }),

        getWarrantyAuditDocumentsById: builder.query({
            query: (id) => ({
                url: 'v1/WarrantyAudit/GetWarrantyAuditDocumentsById',
                method: 'GET',
                params: {
                    Id: id
                }
            }),
            transformResponse: (response) => unwrapArray(response),
            providesTags: (result, error, id) => [{ type: 'WarrantyAuditResult', id: `documents-${id}` }]
        }),

        deleteWarrantyAuditDocument: builder.mutation({
            query: (body) => ({
                url: 'v1/WarrantyAudit/DeleteWarrantyAuditDocument',
                method: 'DELETE',
                body
            }),
            invalidatesTags: (result, error, { WarrantyAuditResultId }) => [{ type: 'WarrantyAuditResult', id: `documents-${WarrantyAuditResultId}` }]
        }),

        getWarrantyAuditResults: builder.query({
            query: ({ bosBranchCode, bosBranchSiteCode, fromDate, toDate, languageId } = {}) => ({
                url: 'v1/WarrantyAudit/results',
                method: 'GET',
                params: {
                    ...(bosBranchCode ? { bosBranchCode } : {}),
                    ...(bosBranchSiteCode ? { bosBranchSiteCode } : {}),
                    ...(fromDate ? { fromDate } : {}),
                    ...(toDate ? { toDate } : {}),
                    ...(languageId ? { languageId } : {})
                }
            }),
            transformResponse: (response) => unwrapArray(response),
            providesTags: ['WarrantyAuditResult']
        }),

        getWarrantyAuditResultById: builder.query({
            query: ({ id, languageId } = {}) => ({
                url: `v1/WarrantyAudit/results/${id}`,
                method: 'GET',
                params: { ...(languageId ? { languageId } : {}) }
            }),
            transformResponse: (response) => unwrap(response),
            providesTags: ['WarrantyAuditResult']
        }),

        getWarrantyAuditHistory: builder.query({
            query: ({ bosBranchCode, bosBranchSiteCode, languageId } = {}) => ({
                url: 'v1/WarrantyAudit/results/history',
                method: 'GET',
                params: {
                    ...(bosBranchCode ? { bosBranchCode } : {}),
                    ...(bosBranchSiteCode ? { bosBranchSiteCode } : {}),
                    ...(languageId ? { languageId } : {})
                }
            }),
            transformResponse: (response) => unwrap(response),
            providesTags: ['WarrantyAuditResult']
        }),

        getWarrantyAuditPdfData: builder.query({
            query: ({ id, languageId } = {}) => ({
                url: `v1/WarrantyAudit/results/${id}/pdf-data`,
                method: 'GET',
                params: { ...(languageId ? { languageId } : {}) }
            }),
            transformResponse: (response) => unwrap(response)
        })
    }),
    overrideExisting: false
});

export const {
    useGetWarrantyAuditCriteriaQuery,
    useLazyGetWarrantyAuditCriteriaQuery,
    useCreateWarrantyAuditCriteriaMutation,
    useUpdateWarrantyAuditCriteriaMutation,
    useDeleteWarrantyAuditCriteriaMutation,
    useCreateWarrantyAuditSubCriteriaMutation,
    useUpdateWarrantyAuditSubCriteriaMutation,
    useDeleteWarrantyAuditSubCriteriaMutation,
    useCreateWarrantyAuditEvaluationMutation,
    useUpdateWarrantyAuditEvaluationMutation,
    useDeleteWarrantyAuditEvaluationMutation,
    useGetWarrantyAuditTemplateQuery,
    useGetWarrantyAuditScoreBandsQuery,
    useCreateWarrantyAuditResultMutation,
    useUploadWarrantyAuditDocumentMutation,
    useGetWarrantyAuditDocumentsByIdQuery,
    useDeleteWarrantyAuditDocumentMutation,
    useGetWarrantyAuditResultsQuery,
    useGetWarrantyAuditResultByIdQuery,
    useLazyGetWarrantyAuditResultByIdQuery,
    useGetWarrantyAuditHistoryQuery,
    useLazyGetWarrantyAuditHistoryQuery,
    useGetWarrantyAuditPdfDataQuery,
    useLazyGetWarrantyAuditPdfDataQuery
} = warrantyAuditApi;
